import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getMessaging } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

/**
 * VAPID Key for FCM to send push notifications
 */
export const VAPID_KEY = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

/**
 * Get current environment
 */
const getCurrentEnvironment = () => {
  // Check for explicit environment variable
  if (process.env.NEXT_PUBLIC_ENVIRONMENT) {
    return process.env.NEXT_PUBLIC_ENVIRONMENT;
  }

  // Check for NODE_ENV
  if (process.env.NODE_ENV === 'development') {
    return 'local';
  }

  // Check for deployment environment (from GitHub Actions)
  if (process.env.NEXT_PUBLIC_DEPLOY_ENV) {
    return process.env.NEXT_PUBLIC_DEPLOY_ENV;
  }

  // Default to qa for safety
  return 'qa';
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const auth = getAuth(app);

/**
 * Returns a Firebase Messaging instance, or null when this browser can't support it.
 *
 * MUST NOT be called at module scope. @firebase/messaging's WindowMessagingFactory
 * does an unguarded `navigator.serviceWorker.addEventListener(...)`. iOS WKWebViews —
 * the Facebook, Instagram and Google app in-app browsers — do not expose
 * navigator.serviceWorker, so getMessaging() throws a synchronous TypeError there.
 * At module scope that aborts evaluation of the root client chunk before React
 * mounts: blank page, uncatchable by any ErrorBoundary.
 */
export const getMessagingInstance = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  // Cheap synchronous mirror of what the SDK factory touches at construction.
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return null;
  }

  try {
    return getMessaging(app);
  } catch (error) {
    console.warn('FCM: messaging unavailable in this browser:', error);
    return null;
  }
};

// Export environment info for debugging
export const getEnvironmentInfo = () => ({
  currentEnv: getCurrentEnvironment(),
  firebaseConfig: firebaseConfig,
  vapidKey: VAPID_KEY,
});
